// src/pages/AdminDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LogoutIcon from "@mui/icons-material/Logout";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { DataGrid } from "@mui/x-data-grid";
import { api, getToken, clearToken } from "../api";

export default function AdminDashboard() {
  const [rows, setRows] = useState([]);
  const token = getToken(); // used by api({ auth: true })

  async function fetchData() {
    try {
      const data = await api("/api/registrations", { auth: true });
      setRows(data);
    } catch (e) {
      window.location.href = "/admin-login";
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  async function updateConfirmed(id, value) {
    // optimistic UI
    setRows(prev => prev.map(r => (r.id === id ? { ...r, confirmed: value } : r)));
    try {
      const updated = await api(`/api/registrations/${id}`, {
        method: "PATCH",
        auth: true,
        body: { confirmed: value },
      });
      setRows(prev => prev.map(r => (r.id === id ? updated : r)));
    } catch (e) {
      console.error("Confirm update failed:", e);
      alert("Update failed");
      setRows(prev => prev.map(r => (r.id === id ? { ...r, confirmed: !value } : r)));
    }
  }

  async function handleDelete(id) {
    const row = rows.find(r => r.id === id);
    if (!row) return;
    if (row.confirmed) return;

    if (!window.confirm(`Delete registration for "${row.team}"? This cannot be undone.`)) {
      return;
    }

    const prevRows = rows;
    setRows(prev => prev.filter(r => r.id !== id));

    try {
      await api(`/api/registrations/${id}`, { method: "DELETE", auth: true });
    } catch (e) {
      console.error("Delete failed:", e);
      alert("Delete failed: " + e.message);
      setRows(prevRows);
    }
  }

  const columns = useMemo(
    () => [
      {
        field: "created_at",
        headerName: "Submitted",
        flex: 1,
        valueFormatter: params =>
          params.value ? new Date(params.value).toLocaleString() : "",
      },
      { field: "team", headerName: "Team", flex: 1 },
      { field: "vehicle", headerName: "Vehicle", flex: 1 },
      { field: "category", headerName: "Category", width: 120 },
      { field: "email", headerName: "Email", flex: 1 },
      { field: "phone", headerName: "Phone", width: 140 },
      { field: "driver_name", headerName: "Driver", flex: 1, minWidth: 150 },
      { field: "driver_blood", headerName: "Driver Blood", width: 130 },
      { field: "driver_shirt", headerName: "Driver Shirt", width: 130 },
      { field: "co_driver_name", headerName: "Co-driver", flex: 1, minWidth: 150 },
      { field: "co_driver_blood", headerName: "Co-driver Blood", width: 150 },
      { field: "co_driver_shirt", headerName: "Co-driver Shirt", width: 150 },
      {
        field: "confirmed",
        headerName: "Confirmed",
        width: 130,
        sortable: false,
        renderCell: p => (
          <Checkbox
            checked={Boolean(p.row?.confirmed)}
            onChange={e => updateConfirmed(p.row.id, e.target.checked)}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        renderCell: p => {
          const isConfirmed = Boolean(p.row?.confirmed);
          return (
            <Tooltip title={isConfirmed ? "Confirmed — cannot delete" : "Delete"}>
              <span>
                <IconButton
                  aria-label="delete"
                  onClick={() => handleDelete(p.row.id)}
                  disabled={isConfirmed}
                  color={isConfirmed ? "default" : "error"}
                  size="small"
                >
                  <DeleteForeverIcon />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [rows]
  );

  function logout() {
    clearToken();
    window.location.href = "/admin-login";
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h5">Registrations</Typography>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Logout">
            <IconButton onClick={logout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ height: "75vh" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={r => r.id}
          disableRowSelectionOnClick
        />
      </Paper>

      <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
        <Button onClick={fetchData} startIcon={<RefreshIcon />}>
          Refresh
        </Button>
        <Button onClick={logout} startIcon={<LogoutIcon />}>
          Logout
        </Button>
      </Box>
    </Box>
  );
}
